/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useEffect, useRef } from "react";

//@Packages
import { useDispatch } from "react-redux";
import { OidcProvider, OidcSecure } from "@axa-fr/react-oidc";
import { Routes, Route } from "react-router-dom";

//@Components
import ViewDevice from "./../../components/viewdevice";
import EditDevice from "./../../components/editdevice";
import LandingPage from "./../../components/landingpage";
import CreateDevice from "./../../components/createdevice";
import LogoutStandAlone from "../../components/logOut/LogoutStandAlone";

//@Constants
import { RBM_ACCESS_TOKEN_STORAGE_NAME, ROUTE_PATH } from "./../../utils/constants";

//@Actions
import { setRBMAccessToken } from "../../actions";

const withOidcSecure = (
    Component: React.ComponentType,
    props?: { type?: string; loadType?: string; clearToastRef?: React.RefObject<() => void> }
) => {
    if (props?.type === "create") {
        return (
            <OidcSecure>
                <CreateDevice
                    type={"standalone"}
                    loadType={props?.loadType || ""}
                    setClearToast={(clearToastFn: () => void) => {
                        if (props.clearToastRef) {
                            if (props.clearToastRef && 'current' in props.clearToastRef) {
                                (props.clearToastRef as React.MutableRefObject<() => void>).current = clearToastFn; // Set the clearToast function reference
                            }
                        }
                    }}
                />
            </OidcSecure>
        );
    } else {
        return (
            <OidcSecure>
                <Component />
            </OidcSecure>
        );
    }
};

//@Functional Component
const StandAloneRoutes = (props?: {
    loadType?: string,
    type?: string,  // Optional prop to determine if it's a standalone create device

    clearToastRef?: React.RefObject<() => void>  // Optional prop to pass clearToast function reference
}) => {
    const dispatch = useDispatch();
    const clearToastRef = useRef<() => void>(); // Reference to the clearToast function in CreateDevice

    useEffect(() => {
        const tokenVal = JSON.parse(sessionStorage.getItem("oidc.default") || '{}')?.tokens?.accessToken || '';

        sessionStorage.setItem(RBM_ACCESS_TOKEN_STORAGE_NAME, tokenVal);

        dispatch(setRBMAccessToken({ rbmAccessToken: tokenVal }));
    }, [sessionStorage.getItem("oidc.default")]);

    interface OidcEventData {
        configurationName: string;
        eventName: string;
        data: unknown;
    }

    const handleOidcEvent = (configurationName: OidcEventData["configurationName"], eventName: OidcEventData["eventName"], data: OidcEventData["data"]): void => {
        switch (eventName) {
            case "token_timer":
                if (data && typeof data === "object" && "timeLeft" in data) {
                    const TIME_LEFT_THRESHOLD = 150; // seconds
                    const timeLeft = (data as { timeLeft: number }).timeLeft;
                    if (timeLeft <= TIME_LEFT_THRESHOLD) {
                        console.warn("Token is about to expire. Clearing toast notifications.");
                        if (clearToastRef.current) {
                            clearToastRef.current();
                        }
                    }
                }
                break;

            case "refreshTokensAsync_begin":
                console.log("Token refresh process started.");
                break;

            case "refreshTokensAsync_error":
                if (clearToastRef.current) {
                    clearToastRef.current();
                }
                break;

            default:
                console.log(`Unhandled OIDC event: ${eventName}`);
                break;
        }
    };

    return (
        <OidcProvider onEvent={handleOidcEvent}>
            <Routes>
                <Route path={ROUTE_PATH.standAloneLanding} element={withOidcSecure(CreateDevice, {
                    type: "create", loadType: props?.loadType,
                    clearToastRef: clearToastRef
                })} />

                <Route path={ROUTE_PATH.deviceslist} element={withOidcSecure(LandingPage)} />
                <Route path={`${ROUTE_PATH.editDevice}/:id`} element={withOidcSecure(EditDevice)} />
                <Route path={`${ROUTE_PATH.viewDevice}/:id`} element={withOidcSecure(ViewDevice)} />
                <Route path={ROUTE_PATH.logout} element={withOidcSecure(LogoutStandAlone)} />
            </Routes>
        </OidcProvider>

    );
};

export default StandAloneRoutes;